import { KVTagMetadata } from './KVAnnotation'

export interface WorldPosition {
  x: number
  y: number
  z: number
}

export interface Quaternion {
  x: number
  y: number
  z: number
  w: number
}

export interface Transform {
  location: WorldPosition
  rotation: Quaternion | number[]
  scale: WorldPosition
}

export interface BoxData {
  width: number
  depth: number
  height: number
}

export interface CylinderData {
  radius: number
  height: number
}

export interface SphereData {
  radius: number
}

export interface PlaneData {
  width: number
  height: number
}

export enum KVPrimitiveGeometry {
  BOX = 'box',
  CYLINDER = 'cylinder',
  SPHERE = 'sphere',
  PLANE = 'plane',
}

export type KVPrimitiveGeometryType = `${KVPrimitiveGeometry}`

export function getPrimitiveGeometryDisplayText(
  geometry: KVPrimitiveGeometryType
): string {
  switch (geometry) {
    case KVPrimitiveGeometry.BOX:
      return 'Box'
    case KVPrimitiveGeometry.CYLINDER:
      return 'Cylinder'
    case KVPrimitiveGeometry.SPHERE:
      return 'Sphere'
    case KVPrimitiveGeometry.PLANE:
      return 'Plane'
    default:
      return 'Box'
  }
}

/**
 * Gets all primitive geometry options for use in dropdowns/selects.
 */
export function getPrimitiveGeometryOptions(): Array<{
  label: string
  value: KVPrimitiveGeometryType
}> {
  return [
    { label: 'Box', value: KVPrimitiveGeometry.BOX },
    { label: 'Cylinder', value: KVPrimitiveGeometry.CYLINDER },
    { label: 'Sphere', value: KVPrimitiveGeometry.SPHERE },
    { label: 'Plane', value: KVPrimitiveGeometry.PLANE },
  ]
}

export interface Metrics {
  points_used?: number
  fit_quality?: number
  rms_distance?: number
  max_distance?: number
  mean_distance?: number
}

export interface NDEData {
  [key: string]: number[][] // e.g., "ultrasonic": [[1,2,3], [4,5,6]]
}

export class KVPrimitive {
  id: string
  label: string
  notes: string
  geometry: KVPrimitiveGeometryType
  transform: Transform
  box_data?: BoxData
  cylinder_data?: CylinderData
  sphere_data?: SphereData
  plane_data?: PlaneData
  metrics?: Metrics
  area?: number
  volume?: number
  userName: string
  userEmail: string
  createdAt: string
  visible: boolean
  ndeData?: NDEData
  tags: Record<string, KVTagMetadata>

  constructor(
    id: string,
    geometry: KVPrimitiveGeometryType = KVPrimitiveGeometry.BOX
  ) {
    this.id = id
    this.geometry = geometry
    this.label = `New ${getPrimitiveGeometryDisplayText(geometry)}`
    this.notes = ''
    this.visible = true
    this.userName = ''
    this.userEmail = ''
    this.createdAt = new Date().toISOString()
    this.tags = {}
    this.transform = {
      location: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0, w: 1 },
      scale: { x: 1, y: 1, z: 1 },
    }
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  static fromJSON(json: any): KVPrimitive {
    const geometry =
      (json.geometry as KVPrimitiveGeometryType) || KVPrimitiveGeometry.BOX
    const primitive = new KVPrimitive(json.primitiveId as string, geometry)

    if (json.primitiveLabel) primitive.label = json.primitiveLabel
    if (json.primitiveNotes) primitive.notes = json.primitiveNotes
    if (json.primitiveVisible !== undefined)
      primitive.visible = json.primitiveVisible
    if (json.primitiveUserName) primitive.userName = json.primitiveUserName
    if (json.primitiveUserEmail) primitive.userEmail = json.primitiveUserEmail
    if (json.primitiveCreatedAt) primitive.createdAt = json.primitiveCreatedAt

    if (json.transform) {
      primitive.transform = json.transform as Transform
    }

    if (json.box_data) {
      primitive.box_data = json.box_data as BoxData
    }

    if (json.cylinder_data) {
      primitive.cylinder_data = json.cylinder_data as CylinderData
    }

    if (json.sphere_data) {
      primitive.sphere_data = json.sphere_data as SphereData
    }

    if (json.plane_data) {
      primitive.plane_data = json.plane_data as PlaneData
    }

    if (json.metrics) {
      primitive.metrics = json.metrics as Metrics
    }

    // Read area and volume from Unreal (eventually)
    if (json.primitiveArea !== undefined) primitive.area = json.primitiveArea
    if (json.primitiveVolume !== undefined)
      primitive.volume = json.primitiveVolume

    if (json.ndeData) {
      primitive.ndeData = json.ndeData as NDEData
    }

    primitive.tags =
      (json.primitiveTags as Record<string, KVTagMetadata> | undefined) ?? {}

    return primitive
  }

  static toJSON(primitive: KVPrimitive): any {
    return {
      primitiveId: primitive.id,
      primitiveLabel: primitive.label,
      primitiveNotes: primitive.notes,
      primitiveVisible: primitive.visible,
      primitiveUserName: primitive.userName,
      primitiveUserEmail: primitive.userEmail,
      primitiveCreatedAt: primitive.createdAt,
      geometry: primitive.geometry,
      transform: primitive.transform,
      box_data: primitive.box_data,
      cylinder_data: primitive.cylinder_data,
      sphere_data: primitive.sphere_data,
      plane_data: primitive.plane_data,
      metrics: primitive.metrics,
      primitiveArea: primitive.area,
      primitiveVolume: primitive.volume,
      ndeData: primitive.ndeData,
      primitiveTags: primitive.tags,
    }
  }
}
